/**
 * "Pick your number" on the sign-up form: in the Android app, Google's phone-number sheet lists the
 * numbers on the phone (each SIM and the Google accounts' numbers) and returns the one the person taps.
 * Native plugin: `native-android/PhoneHintPlugin.java`. No permission is needed and the app never reads
 * the SIMs itself. A browser cannot read a phone's numbers, so this is app-only; an older installed app
 * has no plugin, and every call then quietly returns nothing and the form is the typed input it was.
 */

import { useEffect, useRef } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { SIGNUP_DIAL_CODES } from '../routes/auth/signupPhone.js';

interface PhoneHintPlugin {
  request(): Promise<{
    phone?: string;
    /** The sheet was shown and closed without a choice. */
    dismissed?: boolean;
    /** Google would not show the sheet; the exception, for logcat. */
    unavailable?: string;
    statusCode?: number;
  }>;
}
/** Registered on first use, so merely importing this file (the sign-up page does) touches no native bridge. */
let plugin: PhoneHintPlugin | null = null;
const phoneHintPlugin = (): PhoneHintPlugin =>
  (plugin ??= registerPlugin<PhoneHintPlugin>('PhoneHint'));

function log(message: string): void {
  try {
    console.warn(`[PhoneHint] ${message}`);
  } catch {
    // No console: nothing to report to.
  }
}

/**
 * True in the app build that has the native plugin. Deliberately NOT turned off for good after one
 * refusal from Google, as it once was: any failure (a rate limit right after a dismissal, Play
 * services still starting, a transient error) latched the sheet off for the rest of the app's life,
 * so the one land-time attempt failing meant tapping the field never offered it again. Each screen
 * already offers it at most once on landing and once on a tap, so a phone with no number costs at
 * most those two silent, invisible calls.
 */
export function phoneHintAvailable(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PhoneHint');
  } catch {
    return false;
  }
}

/**
 * `+919876543210` as the calling code and the national digits, for the sign-up form's two inputs.
 *
 * A device whose own number Android reports without a leading `+` (a bare "917977396258" for an
 * Indian SIM, say) forces Google's hint API to guess where the number's own country code ends —
 * and it guesses from the device's configured region, not the digits. On a phone whose region is
 * set to one country but whose SIM is from another, that guess is wrong: it hands back its own
 * calling code glued in front of the number's own, e.g. `+44` immediately followed by the number's
 * real `91…`, as `+44917977396258`. When the digits right after Google's guessed code themselves
 * start with another known calling code AND form a fully valid mobile number for THAT country, that
 * reading is preferred over Google's: a coincidence this exact would be vanishingly rare, so it is
 * almost always the number's own code Google mis-detected the start of, not a second, genuine one.
 */
export function splitE164(e164: string): { dialCode: string; national: string } | null {
  const digits = e164.replace(/[^\d+]/g, '');
  if (!digits.startsWith('+')) return null;
  const rest = digits.slice(1);
  // Longest code first: "+971" must not be read as "+9".
  const byLength = [...SIGNUP_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  const country = byLength.find((c) => rest.startsWith(c.code));
  if (!country) return null;
  const national = rest.slice(country.code.length);
  if (!national) return null;
  const embedded = byLength.find(
    (c) =>
      c.code !== country.code &&
      national.startsWith(c.code) &&
      c.national.test(national.slice(c.code.length)),
  );
  if (embedded) return { dialCode: embedded.code, national: national.slice(embedded.code.length) };
  return { dialCode: country.code, national };
}

/** `+919876543210` as `+91 98765 43210`-ish for reading back; anything else as it is. */
export function shownPhone(e164: string): string {
  const split = splitE164(e164);
  return split ? `+${split.dialCode} ${split.national}` : e164;
}

let inFlight: Promise<{ dialCode: string; national: string } | null> | null = null;

/**
 * The number the person chose, split for the form; null when nothing was chosen or the sheet cannot
 * be shown. One sheet at a time: a tap on the field while the on-arrival request is still loading
 * shares that request instead of starting a second, which the native side would treat as replacing
 * the first (so neither sheet showed).
 */
export function askPhoneHint(): Promise<{ dialCode: string; national: string } | null> {
  inFlight ??= requestPhoneHint().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function requestPhoneHint(): Promise<{ dialCode: string; national: string } | null> {
  if (!phoneHintAvailable()) {
    log('not offered: no native PhoneHint plugin in this build');
    return null;
  }
  try {
    const result = await phoneHintPlugin().request();
    if (result?.unavailable) {
      log(`Google did not show the sheet: ${result.unavailable}`);
      return null;
    }
    if (result?.dismissed) {
      log('sheet closed without a choice');
      return null;
    }
    if (typeof result?.phone !== 'string') return null;
    const picked = pickedNumber(result.phone);
    // The number is never logged, only whether it could be used.
    log(picked ? 'number picked' : 'picked a number sign-up cannot read (country not offered?)');
    return picked;
  } catch (error) {
    log(`request failed: ${String(error)}`);
    return null;
  }
}

/**
 * Google's answer as the form's two inputs. Documented as E.164, but a SIM that reports its number
 * without the `+` comes back as bare digits ("919876543210"): with more digits than any national
 * number, those already start with the calling code, so they are read as if the `+` were there.
 */
export function pickedNumber(phone: string): { dialCode: string; national: string } | null {
  const split = splitE164(phone);
  if (split) return split;
  const digits = phone.replace(/\D/g, '');
  return !phone.trim().startsWith('+') && digits.length > 10 ? splitE164(`+${digits}`) : null;
}

/**
 * Tapping an empty mobile-number field opens Google's sheet of the phone's numbers, the way other
 * apps do, instead of a separate button. Offered once per screen: a person who closes the sheet is
 * typing their number, and must not get it back on every tap. The field is blurred while the sheet
 * is up so the keyboard does not open behind it. Does nothing in a browser.
 */
export function usePhoneHintOnTap(
  onPicked: (picked: { dialCode: string; national: string }) => void,
): (input: HTMLInputElement, empty: boolean) => void {
  const offered = useRef(false);
  return (input, empty) => {
    if (offered.current || !empty || !phoneHintAvailable()) return;
    offered.current = true;
    input.blur();
    void askPhoneHint().then((picked) => {
      if (picked) onPicked(picked);
      // Closed without a choice: back to the field for typing.
      else input.focus();
    });
  };
}

/**
 * Opens Google's sheet of the phone's numbers the moment the sign-up form itself appears — no tap
 * needed. `enabled` is true exactly while this form (not a different step of the same screen) is
 * showing, so re-showing the same form (e.g. after "Change" on an SSO sign-up) offers it again, but
 * navigating away and back within one mount does not. `empty` is read once, at the moment it turns
 * on: a number the form already carries (from SSO prefill, a picker-filled field after "Change") is
 * never overwritten. Nothing is focused first, so the sheet is not followed by a keyboard. Does
 * nothing in a browser.
 */
export function usePhoneHintOnLand(
  enabled: boolean,
  empty: boolean,
  onPicked: (picked: { dialCode: string; national: string }) => void,
): void {
  const offered = useRef(false);
  const wasEnabled = useRef(false);
  const onPickedRef = useRef(onPicked);
  onPickedRef.current = onPicked;

  useEffect(() => {
    if (enabled && !wasEnabled.current) offered.current = false;
    wasEnabled.current = enabled;
    if (offered.current || !enabled || !empty || !phoneHintAvailable()) return;
    offered.current = true;
    void askPhoneHint().then((picked) => {
      if (picked) onPickedRef.current(picked);
    });
    // `empty` is deliberately not a dependency: typing after the sheet has been offered must not
    // re-trigger it, which excluding it from here (and from `offered`'s reset) guarantees.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
